import { Component } from 'react'
import { connect } from 'react-redux'

// Actions
import { actions as cmsActions } from 'modules/cms/actions'

// Components
import { PageHeading, Spinner, EmptyState, Section } from '@tracklib/kit'
import SEO from 'components/base/seo'
import ArticleItem from 'components/articles/item'

// Selectors
import { selectRequest } from 'modules/requests/selectors'

// Types
import { ApiRequest, CmsPage, Dispatch, InitialDataProps, State } from 'types'

import styles from './bloglist.css'

export const sortArticles = (articles: Array<CmsPage>): Array<CmsPage> =>
  articles
    .filter(x => !x.hide)
    .sort((a, b) => new Date(b.meta.first_published_at).getTime() - new Date(a.meta.first_published_at).getTime())

const slugToString = (slug: string): string => {
  const string = slug.replace(/-/g, ' ')
  return string.charAt(0).toUpperCase() + string.slice(1)
}

type BlogListPageProps = {
  match: {
    params: { categorySlug: string }
  }
  // mapStateToProps
  categorySlug?: string
  articles: Array<CmsPage>
  fetchArticlesRequest: ApiRequest
  // mapDispatchToProps
  fetchArticleSummariesByCategory: (id: string) => void
  fetchAllArticleSummaries: () => void
}

class BlogListPage extends Component<BlogListPageProps> {
  static loadInitialData({
    location: {
      params: { categorySlug },
    },
  }: InitialDataProps<{ categorySlug: string }>) {
    if (categorySlug) {
      return (dispatch: Dispatch) => dispatch(cmsActions.fetchArticleSummariesByCategory(categorySlug))
    }
    return (dispatch: Dispatch) => dispatch(cmsActions.fetchAllArticleSummaries())
  }

  componentDidMount() {
    const { categorySlug, fetchArticleSummariesByCategory, fetchAllArticleSummaries, fetchArticlesRequest } = this.props
    if (!fetchArticlesRequest.isDone) {
      if (categorySlug) {
        fetchArticleSummariesByCategory(categorySlug)
      } else {
        fetchAllArticleSummaries()
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: BlogListPageProps) {
    const { fetchArticleSummariesByCategory, fetchAllArticleSummaries, categorySlug } = this.props
    if (categorySlug !== nextProps.categorySlug) {
      if (nextProps.categorySlug) {
        fetchArticleSummariesByCategory(nextProps.categorySlug)
      } else {
        fetchAllArticleSummaries()
      }
    }
  }

  render() {
    const { categorySlug, articles, fetchArticlesRequest } = this.props
    return (
      <div>
        <Section extraPad className={styles.background}>
          <SEO title="Sample blog | Tracklib" description="Learn about sampling and music on Tracklib." />
          <PageHeading heading="Blog" subheading={categorySlug ? slugToString(categorySlug) : 'All'} noPad />
          {articles && fetchArticlesRequest.isDone && (
            <ul className={styles.list} datatesting="bloglist_list">
              {articles.map(x => (
                <ArticleItem article={x} key={x.id} />
              ))}
            </ul>
          )}
          {fetchArticlesRequest.isLoading && (
            <ul className={styles.spinner}>
              <Spinner minHeight={400} />
            </ul>
          )}
          {fetchArticlesRequest.isDone && !articles.length && <EmptyState text="No posts found" emoji="looking" />}
        </Section>
      </div>
    )
  }
}

const mapStateToProps = (
  state: State,
  {
    match: {
      params: { categorySlug },
    },
  }: BlogListPageProps,
) => {
  const { pageSummaries } = state.entities
  const articlesList = Object.keys(pageSummaries).map(id => pageSummaries[id])
  const articles = categorySlug
    ? articlesList.filter(article => article.categories?.some(articleCategory => articleCategory.slug === categorySlug))
    : articlesList
  return {
    categorySlug,
    fetchArticlesRequest: selectRequest(state, categorySlug ? 'FETCH_ARTICLE_SUMMARIES_BY_CATEGORY' : 'FETCH_ALL_ARTICLE_SUMMARIES'),
    articles: sortArticles(articles),
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    fetchArticleSummariesByCategory: (categorySlug: string) => dispatch(cmsActions.fetchArticleSummariesByCategory(categorySlug)),
    fetchAllArticleSummaries: () => dispatch(cmsActions.fetchAllArticleSummaries()),
  }
}

export { BlogListPage as Component }
export default connect(mapStateToProps, mapDispatchToProps)(BlogListPage)
